import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { useAppLaunchpadService } from '$hooks/services'
import { TAppLaunchpad } from '$services/api'
import { Button, Spinner } from '@genie-fintech/ui/components'
import { BaseText as BaseHookFieldText } from '@genie-fintech/ui/components/hook-fields'
import Textarea from '@genie-fintech/ui/components/hook-fields/Textarea'
import { Icon } from '@genie-fintech/ui/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMount } from 'ahooks'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import Loading from '$components/Loading'
import DeleteWithConfirm from '$components/DeleteWithConfirm'
import { flexColumn, flexRow } from '$styles/common.css'
import { useAppDetailStore } from '$hooks/stores'
import DescriptionTableColumn from '$components/DescriptionTableColumn'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { pluralize } from '$app/utils'
import Header from '$layouts/LayoutWithSidebar/Header'
import { form, innerMain, main } from './styles.css'
import { GalleryHorizontalEnd, Plus } from 'lucide-react'
import { footnote, title } from '@genie-fintech/ui/style/typography'
import EmptyData from '$components/EmptyData'
import Table, { Column } from '$components/Table'
import Drawer from '$components/Drawer'
import { useBreadcrumb } from '$components/Breadcrumb/hooks/useBreadcrumb'
import { usePermissions } from '$hooks/actions'
import { PERMISSIONS } from '$app/constants'

const { colors } = themeVars

const schema = z.object({
  name: z.string().trim().min(1, 'Required!'),
  url: z.string().trim().min(1, 'Required!').url('Invalid URL!'),
  description: z.string().trim()
})

type TFormValues = z.infer<typeof schema>

const INITIAL_DEFAULT_VALUES: TFormValues = {
  name: '',
  url: '',
  description: ''
}

const AppLaunchPadSetting = () => {
  const [selectedID, setSelectedID] = useState<number>()

  const [open, setOpen] = useState(false)

  const { route } = useRouteSummary()

  const { appId } = route.params

  const appName = useAppDetailStore(state => state.appDetail?.name) ?? ''

  const { checkPermissions } = usePermissions()

  const CAN_EDIT = checkPermissions([PERMISSIONS.APPLICATION_EDIT_LAUNCHPAD])

  const methods = useForm<TFormValues>({
    resolver: zodResolver(schema),
    defaultValues: INITIAL_DEFAULT_VALUES
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty }
  } = methods

  const {
    launchpads,
    fetchAppLaunchpads,
    fetchingAppLaunchpads,

    addAppLaunchpadAsync,
    addingAppLaunchpad,

    updateAppLaunchpadsAsync,
    updatingAppLaunchpads,

    removeAppLaunchpadAsync,
    removingAppLaunchpad
  } = useAppLaunchpadService()

  useMount(() => {
    if (!appId) return
    fetchAppLaunchpads({ application_id: appId })
  })

  useBreadcrumb([{ name: 'Launchpads' }])

  const handleOpenDrawer = useCallback((id?: number) => {
    setOpen(true)
    setSelectedID(id)
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setOpen(false)
    setSelectedID(undefined)
    reset(INITIAL_DEFAULT_VALUES)
  }, [reset])

  const handleOnSubmit = useCallback(
    (formValues: TFormValues) => {
      if (!appId) return

      if (selectedID === undefined) {
        addAppLaunchpadAsync(appId, formValues).then(handleCloseDrawer)
        return
      }

      updateAppLaunchpadsAsync(selectedID, formValues).then(handleCloseDrawer)
    },
    [
      appId,
      selectedID,
      addAppLaunchpadAsync,
      updateAppLaunchpadsAsync,
      handleCloseDrawer
    ]
  )

  const handleOnEdit = useCallback(
    (id: number) => {
      handleOpenDrawer(id)
      reset(
        launchpads.find(launchpad => launchpad.id === id) ??
          INITIAL_DEFAULT_VALUES
      )
    },
    [handleOpenDrawer, launchpads, reset]
  )

  const handleOnDelete = useCallback(
    (id: number) => {
      setSelectedID(id)

      removeAppLaunchpadAsync(id).finally(() => {
        setSelectedID(undefined)
      })
    },
    [appId, launchpads, removeAppLaunchpadAsync]
  )

  const isEmpty = !fetchingAppLaunchpads && !launchpads.length
  const hasData = !fetchingAppLaunchpads && !!launchpads.length

  const loading = [addingAppLaunchpad, updatingAppLaunchpads].some(
    loading => loading
  )

  const disabled = loading || !CAN_EDIT

  const columns: Column<TAppLaunchpad>[] = [
    {
      name: 'NO',
      render: ({ index }) => index + 1
    },
    {
      name: 'NAME',
      dataIndex: 'name'
    },
    {
      name: 'URL',
      dataIndex: 'url'
    },
    {
      name: 'DESCRIPTION',
      dataIndex: 'description',
      render: ({ record }) => (
        <DescriptionTableColumn desc={record.description} />
      )
    },
    {
      name: 'Action',
      render: ({ record }) => (
        <article className={flexRow} style={{ gap: 8 }}>
          <Button
            styleVariants={{
              kind: 'neutral',
              category: 'icon',
              type: 'text'
            }}
            disabled={disabled}
            onClick={() => {
              handleOnEdit(record.id)
            }}
          >
            <Icon namespace="EditPencil" />
          </Button>

          {launchpads.length > 1 && (
            <DeleteWithConfirm
              disabled={disabled}
              loading={selectedID === record.id && removingAppLaunchpad}
              onConfirm={() => {
                handleOnDelete(record.id)
              }}
            />
          )}
        </article>
      )
    }
  ]

  return (
    <>
      <Header style={{ gap: 8 }}>
        <GalleryHorizontalEnd size={20} />

        <span className={title.two} style={{ paddingLeft: 4 }}>
          Launchpads
        </span>

        <span style={{ flex: 1 }} />

        <Button
          onClick={() => handleOpenDrawer()}
          styleVariants={{ size: 'small' }}
          disabled={disabled}
        >
          <Plus size={16} />
          Add Launchpad
        </Button>
      </Header>

      {fetchingAppLaunchpads && <Loading />}

      <main className={main}>
        <article className={innerMain}>
          <header className={flexColumn} style={{ gap: 4 }}>
            <p className={title.two}>{appName}'s LAUNCHPADS</p>
            <p className={footnote.one} style={{ color: colors.neutral[60] }}>
              Access key tools and resources quickly through customizable
              launchpads.
            </p>
          </header>

          {isEmpty && (
            <EmptyData
              type="launchpad"
              onClick={() => (CAN_EDIT ? handleOpenDrawer() : undefined)}
            />
          )}

          {hasData && (
            <article className={flexColumn} style={{ gap: 16 }}>
              <article
                className={flexRow}
                style={{ justifyContent: 'flex-end' }}
              >
                <p
                  className={title.six}
                  style={{ color: colors.text.disabled }}
                >
                  {pluralize(launchpads.length, 'LAUNCHPAD')}
                </p>
              </article>

              <Table
                columns={columns}
                data={launchpads}
                loading={fetchingAppLaunchpads}
              />
            </article>
          )}
        </article>
      </main>

      <Drawer
        direction="right"
        dismissible={false}
        open={open}
        onOpenChange={setOpen}
        title={`${selectedID !== undefined ? 'Edit' : 'Add'} Launchpad`}
        withCloseButton
      >
        <form className={form} onSubmit={handleSubmit(handleOnSubmit)}>
          <article className={flexColumn} style={{ gap: 4 }}>
            <p className="text-xl font-semibold">Launchpad</p>
            <p className="text-xs text-[--colors-neutral-50]">
              Access key tools and resources quickly through customizable
              launchpads.
            </p>
          </article>

          <article className={flexColumn} style={{ gap: 20, flex: 1 }}>
            <BaseHookFieldText
              name="name"
              control={control}
              label="Name"
              required
            />

            <BaseHookFieldText
              name="url"
              control={control}
              label="URL"
              required
            />

            <Textarea
              name="description"
              control={control}
              label="Description"
            />
          </article>

          <Button
            styleVariants={{ inline: false }}
            type="submit"
            disabled={disabled || !isDirty}
          >
            {updatingAppLaunchpads && <Spinner />}
            {selectedID === undefined && <Icon namespace="Add" />}
            {selectedID === undefined ? 'Add New' : 'Save Changes'}
          </Button>
        </form>
      </Drawer>
    </>
  )
}

export default AppLaunchPadSetting
